import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, MapPin, FileText, MessagesSquare } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PROCESS_STEPS, NETWORK_STANDARDS, FAQS } from "@/lib/content";

export function SectionHead({ eyebrow, title, body, center = false }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-3 font-display text-4xl sm:text-5xl text-[#F4F1EA] leading-[1.05]">{title}</h2>
      {body && <p className="mt-4 text-slate-400 text-lg leading-relaxed">{body}</p>}
    </div>
  );
}

export function ProcessSection({ id = "how" }) {
  return (
    <section id={id} className="relative py-20 md:py-28 border-t border-[#272B36] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHead
          eyebrow="How it works"
          title="From a bad day to the right lawyer."
          body="Four steps, and you only pay the attorney you choose to hire."
        />
        <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#272B36] rounded-2xl overflow-hidden border border-[#272B36]">
          {PROCESS_STEPS.map((step, i) => (
            <li key={step.n} data-testid={`process-step-${i}`} className="bg-[#0F1116] p-7 md:p-8">
              <p className="font-display text-5xl text-[#D9B36C]/80 leading-none">{step.n}</p>
              <h3 className="mt-6 font-sans-ui font-semibold text-[#F4F1EA] text-lg">{step.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const STANDARD_ICONS = [BadgeCheck, MapPin, FileText, MessagesSquare];

export function NetworkSection() {
  return (
    <section className="relative py-20 md:py-28 border-t border-[#272B36] bg-[#0F1116] overflow-hidden">
      <div className="absolute inset-0 glow-br pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <SectionHead
            eyebrow="The network"
            title="Vetted attorneys, not a directory."
            body="We don't hand you a list of names. Each request goes to one attorney chosen for that matter, held to the same four standards."
          />
          <Link
            to="/attorneys"
            data-testid="network-join-cta"
            className="mt-8 inline-flex items-center gap-2 text-[#D9B36C] font-medium hover:text-[#F4F1EA] transition-colors"
          >
            Attorneys: apply to join the network <ArrowRight size={16} />
          </Link>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {NETWORK_STANDARDS.map((s, i) => {
            const Icon = STANDARD_ICONS[i] || BadgeCheck;
            return (
              <div key={s.h} className="rounded-2xl border border-[#272B36] bg-[#121419] p-6">
                <Icon size={20} className="text-[#D9B36C]" />
                <p className="mt-4 font-semibold text-[#F4F1EA]">{s.h}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.b}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  return (
    <section className="relative py-20 md:py-28 border-t border-[#272B36]">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="Questions" title="What creators ask first." />
        <Accordion type="single" collapsible className="mt-10" data-testid="home-faq">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-[#272B36]">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-sans-ui font-medium text-[#F4F1EA] text-base sm:text-lg hover:no-underline hover:text-[#D9B36C]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-400 text-base leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
